export type AccountsReferrer = "studio" | "askgvt";

export type AccountsReferrerState = {
	referrer: AccountsReferrer;
	source: string;
};

const STORAGE_KEY = "accountsReferrer";
const STUDIO_PATH_PREFIX = "/studio";

const defaultReferrer = (): AccountsReferrerState => ({
	referrer: "askgvt",
	source: "/",
});

/** Studio vs AskGVT is determined from the page URL the user left, not a stale tag. */
export const inferAccountsReferrerFromSource = (
	source: string,
): AccountsReferrer => {
	try {
		const path = new URL(source, window.location.origin).pathname;
		if (
			path === STUDIO_PATH_PREFIX ||
			path.startsWith(`${STUDIO_PATH_PREFIX}/`)
		) {
			return "studio";
		}
	} catch {
		// Fall through to AskGVT.
	}
	return "askgvt";
};

const withInferredReferrer = (
	state: AccountsReferrerState,
): AccountsReferrerState => ({
	source: state.source,
	referrer: inferAccountsReferrerFromSource(state.source),
});

export const setAccountsReferrer = (
	referrer: AccountsReferrer,
	source: string,
) => {
	localStorage.setItem(STORAGE_KEY, JSON.stringify({ referrer, source }));
};

/** Keep account “Back” in sync while the user browses Studio or AskGVT. */
export const touchAccountsReferrer = (referrer: AccountsReferrer) => {
	setAccountsReferrer(referrer, window.location.href);
};

/** Call before leaving Studio or AskGVT for an account page so Back returns here. */
export const navigateToAccountsPath = (
	path: string,
	referrer: AccountsReferrer = inferAccountsReferrerFromSource(
		window.location.href,
	),
) => {
	setAccountsReferrer(referrer, window.location.href);
	window.location.assign(path);
};

export const getAccountsReferrer = (): AccountsReferrerState => {
	const raw = localStorage.getItem(STORAGE_KEY);
	if (!raw) return defaultReferrer();

	try {
		const parsed = JSON.parse(raw) as Partial<AccountsReferrerState>;
		if (
			(parsed.referrer === "studio" || parsed.referrer === "askgvt") &&
			typeof parsed.source === "string" &&
			parsed.source.length > 0
		) {
			return withInferredReferrer({
				referrer: parsed.referrer,
				source: parsed.source,
			});
		}
	} catch {
		// Fall through for the legacy string-only value.
	}

	if (raw === "studio" || raw === "askgvt") {
		return withInferredReferrer({
			referrer: raw,
			source: raw === "studio" ? "/studio" : "/",
		});
	}

	return defaultReferrer();
};

/** Only same-origin paths are allowed so stored JSON cannot become an open redirect. */
export const resolveAccountsReferrerUrl = (
	state: AccountsReferrerState,
): string => {
	try {
		const url = new URL(state.source, window.location.origin);
		if (url.origin !== window.location.origin) {
			throw new Error("cross-origin referrer");
		}

		return `${url.pathname}${url.search}${url.hash}`;
	} catch {
		return inferAccountsReferrerFromSource(state.source) === "studio"
			? "/studio"
			: "/";
	}
};
